import {
  AGGREGATION_FIELDS,
  ENTITY_TYPES,
  NODE_TYPES,
  type OpenMetadataResponse,
  type OpenMetadataSource,
} from '@/shared/api/openmetadata';

import { ENTITY_TYPE_LABELS } from './transform';
import type { AssetTreeNode } from './types';

type ExpandingNodeParams = {
  key: string;
  entityType: string;
  index: string;
  useAggregation: boolean;
  entityTypes?: string[];
  serviceType?: string;
  serviceName?: string;
  databaseName?: string;
  schemaName?: string;
};

export const buildChildNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  if (expandingNode.useAggregation) {
    return buildAggregationChildNodes(nodeChildren, expandingNode);
  }
  return buildHitsChildNodes(nodeChildren, expandingNode);
};

const buildAggregationChildNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  if (expandingNode.schemaName) {
    return buildSchemaEntityTypeNodes(nodeChildren, expandingNode);
  }
  if (expandingNode.entityType === ENTITY_TYPES.SCHEMA) {
    return buildSchemaBucketNodes(nodeChildren, expandingNode);
  }
  if (expandingNode.serviceType) {
    return buildServiceNameNodes(nodeChildren, expandingNode);
  }
  return buildServiceTypeNodes(nodeChildren, expandingNode);
};

const buildSchemaEntityTypeNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  const entityTypeBuckets = nodeChildren.aggregations?.[AGGREGATION_FIELDS.ENTITY_TYPE]?.buckets || [];

  const childNodes = entityTypeBuckets
    .filter((bucket) => bucket.key !== NODE_TYPES.SCHEMA)
    .map((bucket) => {
      const entityType = bucket.key;
      const label = ENTITY_TYPE_LABELS[entityType] || entityType;

      return {
        key: `${expandingNode.key}-${entityType}-count`,
        title: `${label} ${bucket.doc_count}`,
        type: entityType,
        serviceType: expandingNode.serviceType,
        serviceName: expandingNode.serviceName,
        databaseName: expandingNode.databaseName,
        schemaName: expandingNode.schemaName,
        isLeaf: true,
        children: [],
        data: {
          entityType,
          count: bucket.doc_count,
        },
      } as AssetTreeNode;
    });

  return sortByTitle(childNodes);
};

const buildSchemaBucketNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  const schemaBuckets = nodeChildren.aggregations?.[AGGREGATION_FIELDS.SCHEMA_NAME]?.buckets || [];

  const childNodes = schemaBuckets.map((bucket) => ({
    key: `${expandingNode.key}-schema-${bucket.key}`,
    title: bucket.key,
    type: NODE_TYPES.SCHEMA,
    serviceType: expandingNode.serviceType,
    isLeaf: false,
    children: [],
    data: {
      schemaName: bucket.key,
      serviceType: expandingNode.serviceType,
      serviceName: expandingNode.serviceName,
      databaseName: expandingNode.databaseName,
    },
  }));

  return sortByTitle(childNodes);
};

const buildServiceNameNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  const serviceNameBuckets = nodeChildren.aggregations?.[AGGREGATION_FIELDS.SERVICE_NAME]?.buckets || [];
  const isMlmodelEntity = expandingNode.entityType === ENTITY_TYPES.MLMODEL;
  const isContainerEntity = expandingNode.entityType === ENTITY_TYPES.CONTAINER;
  const shouldShowCount = isMlmodelEntity || isContainerEntity;

  const childNodes = serviceNameBuckets.map((bucket) => ({
    key: `${expandingNode.key}-service-${bucket.key}`,
    title: shouldShowCount ? `${bucket.key} ${bucket.doc_count}` : bucket.key,
    type: NODE_TYPES.SERVICE,
    serviceType: expandingNode.serviceType,
    isLeaf: shouldShowCount,
    children: [],
    data: {
      serviceName: bucket.key,
      serviceType: expandingNode.serviceType,
      parentIndex: expandingNode.index,
      parentEntityType: expandingNode.entityType,
      count: bucket.doc_count,
      originalKey: bucket.key,
    },
  }));

  return sortByOriginalKey(childNodes);
};

const buildServiceTypeNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  const serviceTypeBuckets = nodeChildren.aggregations?.[AGGREGATION_FIELDS.SERVICE_TYPE]?.buckets || [];
  const isTopicEntity = expandingNode.entityType === ENTITY_TYPES.TOPIC;

  const childNodes = serviceTypeBuckets.map((bucket) => ({
    key: `${expandingNode.key}-serviceType-${bucket.key}`,
    title: isTopicEntity ? `${bucket.key} ${bucket.doc_count}` : bucket.key,
    type: NODE_TYPES.SERVICE_TYPE,
    serviceType: bucket.key.toLowerCase(),
    isLeaf: isTopicEntity,
    children: [],
    data: {
      parentEntityType: expandingNode.entityType,
      parentIndex: expandingNode.index,
      serviceType: bucket.key.toLowerCase(),
      count: bucket.doc_count,
      originalKey: bucket.key,
    },
  }));

  return sortByOriginalKey(childNodes);
};

const buildHitsChildNodes = (
  nodeChildren: OpenMetadataResponse,
  expandingNode: ExpandingNodeParams
): AssetTreeNode[] => {
  const hits = nodeChildren.hits?.hits || [];

  const childNodes = hits.map((hit) => {
    const source = hit._source as OpenMetadataSource;

    return {
      key: `${expandingNode.key}-item-${hit._id}`,
      title: source.displayName || source.name || hit._id,
      type: NODE_TYPES.DATABASE,
      serviceType: source.serviceType?.toLowerCase(),
      fullyQualifiedName: source.fullyQualifiedName,
      isLeaf: false,
      data: source,
    };
  });

  return sortByTitle(childNodes);
};

const sortByTitle = (nodes: AssetTreeNode[]): AssetTreeNode[] => {
  return nodes.sort((a, b) => {
    const titleA = typeof a.title === 'string' ? a.title : '';
    const titleB = typeof b.title === 'string' ? b.title : '';
    return titleA.localeCompare(titleB);
  });
};

const sortByOriginalKey = (nodes: AssetTreeNode[]): AssetTreeNode[] => {
  return nodes.sort((a, b) => {
    const aData = a.data as { originalKey?: string };
    const bData = b.data as { originalKey?: string };
    const aKey = aData.originalKey || (typeof a.title === 'string' ? a.title : '');
    const bKey = bData.originalKey || (typeof b.title === 'string' ? b.title : '');
    return aKey.localeCompare(bKey);
  });
};
